import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MagnifyingGlass, BookBookmark, Rss } from "@phosphor-icons/react";

const IOC_TYPES = ["ip", "domain", "url", "hash", "email"];

function confColor(c) {
  if (c >= 80) return "border-rag-red/50 text-rag-red-fg";
  if (c >= 50) return "border-rag-amber/50 text-rag-amber-fg";
  return "border-border text-muted-foreground";
}

export default function SocIntelLibrary() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, count: 0 });
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (type !== "all") p.set("type", type);
    api.get(`/soc/threatintel/library?${p.toString()}`).then(r => {
      setRows(r.data.items || []);
      setMeta({ total: r.data.total || 0, count: r.data.count || 0 });
    }).catch(() => {});
  }, [q, type]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/soc/threatintel/library/stats").then(r => setStats(r.data)).catch(() => {}); }, []);

  return (
    <div className="p-6 space-y-5" data-testid="soc-intel-library-view">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-widest text-rag-red mb-1">Security Operations</div>
        <h1 className="font-display text-3xl font-black flex items-center gap-2"><BookBookmark size={26} /> Threat Intel Library</h1>
        <p className="text-sm text-muted-foreground mt-1">Reference library of known-bad indicators pulled from your subscribed threat-intel feeds. Not attached to any case — analysts check observables against this at the point of entry.</p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="soc-library-stats">
          <Stat label="Indicators" value={stats.total} />
          <Stat label="Active Feeds" value={stats.active_feeds} icon={<Rss size={13} />} />
          <Stat label="IPs" value={stats.by_type?.ip || 0} />
          <Stat label="Domains / URLs" value={(stats.by_type?.domain || 0) + (stats.by_type?.url || 0)} />
        </div>
      )}

      <Card className="p-3 border-border rounded-sm flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input data-testid="soc-library-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search indicator value…" className="rounded-sm h-9 pl-8 text-sm font-mono" />
        </div>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger data-testid="soc-library-type-filter" className="w-36 rounded-sm h-9 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All types</SelectItem>{IOC_TYPES.map(t => <SelectItem key={t} value={t}>{t.toUpperCase()}</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-[11px] font-mono text-muted-foreground px-1">{meta.count} of {meta.total}</span>
      </Card>

      <Card className="border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="soc-library-table">
          <thead className="bg-accent/30 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2.5">Type</th>
              <th className="text-left px-3 py-2.5">Value</th>
              <th className="text-left px-3 py-2.5">Confidence</th>
              <th className="text-left px-3 py-2.5">Malware / Threat</th>
              <th className="text-left px-3 py-2.5">Feeds</th>
              <th className="text-left px-3 py-2.5">Tags</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-t border-border align-top" data-testid={`soc-library-row-${r.id}`}>
                <td className="px-3 py-2.5"><span className="text-[10px] font-mono uppercase px-2 py-0.5 border border-border rounded-sm">{r.type}</span></td>
                <td className="px-3 py-2.5 font-mono max-w-[280px] truncate" title={r.value}>{r.value}</td>
                <td className="px-3 py-2.5"><span className={`text-[10px] font-mono px-2 py-0.5 border rounded-sm ${confColor(r.confidence)}`}>{r.confidence}%</span></td>
                <td className="px-3 py-2.5 text-xs">{r.malware || r.threat_type || "—"}</td>
                <td className="px-3 py-2.5 text-[11px] font-mono text-muted-foreground">{(r.source_feeds || []).join(", ")}</td>
                <td className="px-3 py-2.5">
                  <div className="flex flex-wrap gap-1">
                    {(r.tags || []).slice(0, 4).map(t => <span key={t} className="text-[9px] font-mono px-1.5 py-0.5 border border-border rounded-sm">{t}</span>)}
                    {(r.tags || []).length > 4 && <span className="text-[9px] text-muted-foreground">+{r.tags.length - 4}</span>}
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="px-3 py-10 text-center text-muted-foreground">No indicators in the library yet. Configure feeds under Settings → Threat Intel Feeds.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

const Stat = ({ label, value, icon }) => (
  <Card className="p-3 border-border rounded-sm">
    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1">{icon}{label}</div>
    <div className="text-2xl font-display font-black mt-0.5">{value}</div>
  </Card>
);
